import { ErrorHandler, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import isEqual from 'fast-deep-equal';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  map,
  merge,
  type Observable,
  of,
  scan,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  tap,
} from 'rxjs';

import {
  type ChatContext,
  type ChatContextProvider,
  getChatContextArtifactIdentity,
} from '@blaze-dashboard/ai-chat-domain';

const PROJECT_CONTEXT: ChatContext = { $type: 'project' };

const INITIAL_DISMISSAL_STATE: DismissalState = {
  context: PROJECT_CONTEXT,
  dismissedIdentity: null,
};

@Injectable({ providedIn: 'root' })
export class ChatContextRegistry {
  private readonly errorHandler = inject(ErrorHandler);

  private readonly providers$ = new BehaviorSubject<readonly ChatContextProvider[]>([]);
  private readonly dismissRequests$ = new Subject<void>();
  private readonly liftRequests$ = new Subject<string>();

  private activationStamp = 0;
  private readonly activationOrder = new Map<ChatContextProvider, number>();
  private readonly providerIsActive = new Map<ChatContextProvider, boolean>();

  private readonly arbitratedIdentities$ = new Subject<string>();

  private readonly arbitratedContext$: Observable<ChatContext> = this.providers$.pipe(
    switchMap((providers) =>
      providers.length === 0
        ? of(PROJECT_CONTEXT)
        : combineLatest(providers.map((provider) => this.isolatedContext$(provider))).pipe(
            map((contexts) =>
              arbitrate(
                providers.map((provider, index) => ({
                  priority: provider.priority,
                  activationOrder: this.activationOrder.get(provider) ?? 0,
                  context: contexts[index],
                })),
              ),
            ),
          ),
    ),
    distinctUntilChanged(isEqual),
    tap((context) => this.arbitratedIdentities$.next(getChatContextArtifactIdentity(context))),
    takeUntilDestroyed(),
    shareReplay({ bufferSize: 1, refCount: false }),
  );
  /**
   * Pushed, never pulled: a provider that keeps state tied to one artifact - a primed
   * refinement, say - must be able to drop it once the arbitration names a different artifact,
   * and it cannot learn that by subscribing to `arbitratedContext$`, which is downstream of its
   * own `context$`.
   */
  readonly arbitratedIdentity$: Observable<string> = this.arbitratedIdentities$.asObservable();

  readonly currentContext$: Observable<ChatContext> = merge(
    this.arbitratedContext$.pipe(
      map((context): DismissalEvent => ({ kind: 'contextChanged', context })),
    ),
    this.dismissRequests$.pipe(map((): DismissalEvent => ({ kind: 'dismissRequested' }))),
    this.liftRequests$.pipe(
      map((identity): DismissalEvent => ({ kind: 'liftRequested', identity })),
    ),
  ).pipe(
    scan(reduceDismissal, INITIAL_DISMISSAL_STATE),
    map((state) => (state.dismissedIdentity === null ? state.context : PROJECT_CONTEXT)),
    distinctUntilChanged(isEqual),
    takeUntilDestroyed(),
    shareReplay({ bufferSize: 1, refCount: false }),
  );

  constructor() {
    this.currentContext$.subscribe();
  }

  register(provider: ChatContextProvider): void {
    const providers = this.providers$.getValue();
    if (providers.includes(provider)) {
      return;
    }
    this.providers$.next([...providers, provider]);
  }

  dismissCurrentContext(): void {
    this.dismissRequests$.next();
  }

  liftDismissalFor(identity: string): void {
    this.liftRequests$.next(identity);
  }

  private isolatedContext$(provider: ChatContextProvider): Observable<ChatContext | null> {
    return provider.context$.pipe(
      tap((context) => this.trackActivation(provider, context)),
      startWith(null),
      catchError((error: unknown) => {
        this.errorHandler.handleError(error);
        return of(null);
      }),
    );
  }

  private trackActivation(provider: ChatContextProvider, context: ChatContext | null): void {
    const isActive = context !== null;
    if (isActive && !this.providerIsActive.get(provider)) {
      this.activationOrder.set(provider, ++this.activationStamp);
    }
    this.providerIsActive.set(provider, isActive);
  }
}

interface DismissalState {
  readonly context: ChatContext;
  readonly dismissedIdentity: string | null;
}

interface ArbitrationCandidate {
  readonly priority: number;
  readonly activationOrder: number;
  readonly context: ChatContext | null;
}

type DismissalEvent =
  | { readonly kind: 'contextChanged'; readonly context: ChatContext }
  | { readonly kind: 'dismissRequested' }
  | { readonly kind: 'liftRequested'; readonly identity: string };

function reduceDismissal(state: DismissalState, event: DismissalEvent): DismissalState {
  switch (event.kind) {
    case 'dismissRequested':
      return state.context.$type === 'project'
        ? state
        : { ...state, dismissedIdentity: getChatContextArtifactIdentity(state.context) };
    case 'liftRequested':
      return state.dismissedIdentity === event.identity
        ? { ...state, dismissedIdentity: null }
        : state;
    case 'contextChanged': {
      const dismissedIdentity =
        state.dismissedIdentity !== null &&
        getChatContextArtifactIdentity(event.context) === state.dismissedIdentity
          ? state.dismissedIdentity
          : null;
      return { context: event.context, dismissedIdentity };
    }
  }
}

function arbitrate(candidates: readonly ArbitrationCandidate[]): ChatContext {
  const active = candidates.filter(
    (candidate): candidate is ArbitrationCandidate & { context: ChatContext } =>
      candidate.context !== null,
  );
  if (active.length === 0) {
    return PROJECT_CONTEXT;
  }
  const [first, ...rest] = active;
  return rest.reduce((best, candidate) => (outranks(candidate, best) ? candidate : best), first)
    .context;
}

function outranks(candidate: ArbitrationCandidate, best: ArbitrationCandidate): boolean {
  if (candidate.priority !== best.priority) {
    return candidate.priority > best.priority;
  }
  return candidate.activationOrder > best.activationOrder;
}
